import React, { useEffect, useState } from 'react';
import { FeatureCollection, GeometryObject } from 'geojson';
import { Marker, Popup, useLeaflet } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { POICategory } from '../../types';
import styled from 'styled-components';

export type Property = {
  lat: number;
  lon: number;
  category: POICategory;
  name: string;
};

export type Data = FeatureCollection<GeometryObject, Property>;

export type Props = {
  data?: Data;
  legendHidden?: boolean;
};

const POI: React.FC<Props> = ({ data, legendHidden }) => {
  const ctx = useLeaflet();
  const [zoom, setZoom] = useState<number>(15);

  useEffect(() => {
    const map = ctx?.map;
    if (!map) return;
    setZoom(map.getZoom());
    const cb = () => {
      setZoom(map.getZoom());
    };
    map.on('zoomend', cb);
    return () => void map.off('zoomend', cb);
  }, [ctx]);

  return (
    <>
      {data?.features.map(f => (
        <Marker
          key={f.properties.name}
          position={[f.properties.lat, f.properties.lon]}
          icon={getMarkerIcon(f.properties, zoom)}
        >
          <Popup>{f.properties.name}</Popup>
        </Marker>
      ))}
      {!legendHidden && (
        <LegendBox>
          {Object.values(POICategory).map((p: POICategory) => (
            <POILegendList key={p}>
              <POILegendCircle className={`${getPOIClassName(p)} poiIcon`} />
              <POILegendText>{p}</POILegendText>
            </POILegendList>
          ))}
        </LegendBox>
      )}
    </>
  );
};

const LegendBox = styled.ul`
  width: 160px;
  height: 150px;
  position: absolute;
  bottom: 20px;
  left: 10px;
  margin: 0;
  padding: 8px;
  border-radius: 2px;
  background: #fff;
  color: #333;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  z-index: 800;
`;

const POILegendList = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 8px;
`;

const POILegendCircle = styled.div`
  width: 8px;
  height: 8px;
  background-size: contain;
  background-repeat: no-repeat;
  margin-right: 5px;
`;

const POILegendText = styled.div``;

const getMarkerIcon = (p: Property, zoom?: number) => {
  let html = '<div class="iconWrapper">';
  html += '<div class="icon ' + getPOIClassName(p.category) + '"></div>';
  if (zoom && zoom > 15) html += '<div class="text">' + p.name + '</div>';
  html += '</div>';
  return divIcon({ className: 'marker', html: html });
};

const getPOIClassName = (categoryName: POICategory) => {
  switch (categoryName) {
    case POICategory.Ikoi:
      return 'ikoi';
    case POICategory.NursingHome:
      return 'nursingHome';
    case POICategory.AreaConsultationSupport:
      return 'areaConsultationSupport';
    case POICategory.AreaComprehensiveSupport:
      return 'areaComprehensiveSupport';
    case POICategory.ChildCulture:
      return 'childCulture';
    case POICategory.ChildCareSupport:
      return 'childCareSupport';
    case POICategory.ChildConsultation:
      return 'childConsultation';
    case POICategory.Nursery:
      return 'nursery';
    case POICategory.ElementarySchool:
      return 'elementarySchool';
    case POICategory.JuniorHighSchool:
      return 'juniorHighSchool';
  }
};

export default POI;
